import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PublisherDealType } from 'src/app/planner/enums/PublisherDealType.enum';
import { AdvertiserInfoVO } from 'src/app/planner/interfaces/AdvertiserInfo';
import { DataService } from 'src/app/planner/services/data.service';
import { isEmpty, forEach, map, uniqBy, uniq } from 'lodash';
import { BudgetDistributionType } from './../../../constants/BudgetDistributionType.const';

@Component({
  selector: 'advanced-budget-allocation',
  templateUrl: './advanced-budget-allocation.component.html',
  styleUrls: ['./advanced-budget-allocation.component.scss']
})
export class AdvancedBudgetAllocationComponent implements OnInit {

  @Input() group: any;
  @Input() formArray: FormArray;
  @Input() isNew: boolean;
  @Input() advertiserInfo: AdvertiserInfoVO;

  budgetDistributionType = BudgetDistributionType;
  selectedBudgetDistributionType: any;
  hasAnyInventoryLevelData: boolean;
  isCollapsed: boolean = true;
  currentInvSrcData: any;
  openAuctionData: any;
  amobeePreferredDeals: any[];
  pgDeals: any[];
  privateDeals: any[];
  pgDealData: any;
  amobeePreferredDealData: any[];
  privateDealsData: any[];
  minBudgetExceedsMaxBudget: boolean = false;
  positiveIntegerBudgetRequired: boolean = false;
  budgetValueShouldBeLessThanHundred: boolean = false;

  pgDealSubscription: Subscription;
  inventorySourceSubscription: Subscription;
  amobeePreferredDealsSubscription: Subscription;
  privateDealsSubscription: Subscription;

  minValueArray: Array<any> = [];
  maxValueArray: Array<any> = [];
  minValueTotal: number = 0;
  maxValueTotal: number = 0;

  inventoryData: Array<any> = [];

  constructor(
    private dataService: DataService
  ) { }

  ngOnInit() {
    Promise.all([
      this.dataService.getAvailableTargetDeals(-1, -1, 2, [PublisherDealType.UnreservedFixed, PublisherDealType.PrivateAuction], true),//preferred deals
      this.dataService.getAvailableTargetDeals(this.advertiserInfo.marketId, this.advertiserInfo.id, 2, [PublisherDealType.UnreservedFixed, PublisherDealType.PrivateAuction], false),//private deals
      this.dataService.getDealExchanges(this.advertiserInfo.marketId),// Inventory sources
      this.dataService.getAvailableTargetDeals(this.advertiserInfo.marketId, this.advertiserInfo.id, 2, [PublisherDealType.Guaranteed], false)//Guaranteed
    ]).then((values: any) => {
      this.inventoryData = values;
      if (this.inventoryData && this.inventoryData.length > 0) {
        this.currentInvSrcData = this.inventoryData[2].map(inv => ({
          label: inv.description,
          name: inv.id,
          value: [0, 0],
          allocated : 0
        }));
        this.amobeePreferredDeals = this.inventoryData[0];
        this.privateDeals = this.inventoryData[1];
        this.checkPlanHasInventoryLevelData();
      }
    });

    this.checkPlanHasInventoryLevelData();
    this.pgDealSubscription = this.pgDealsControl.valueChanges.subscribe((val) => {
      if (isEmpty(val)) {
        this.pgDealData = [];
      }
      this.checkPlanHasInventoryLevelData();
    });
    this.amobeePreferredDealsSubscription = this.amobeePreferredDealsControl.valueChanges.subscribe((val) => {
      if (isEmpty(val)) {
        this.amobeePreferredDealData = [];
      }
      this.checkPlanHasInventoryLevelData();
    });
    this.inventorySourceSubscription = this.inventorySourceControl.valueChanges.subscribe((val) => {
      if (isEmpty(val)) {
        this.openAuctionData = [];
      }
      this.checkPlanHasInventoryLevelData();
    });
    this.privateDealsSubscription = this.privateDealsControl.valueChanges.subscribe((val) => {
      if (isEmpty(val)) {
        this.privateDealsData = [];
      }
      this.checkPlanHasInventoryLevelData();
    });

    //Distribution Type dropdown
    if (!this.isNew && this.budgetDistributionTypeControl.value) {
      this.selectedBudgetDistributionType = this.budgetDistributionTypeControl.value;
    } else {
      this.selectedBudgetDistributionType = this.budgetDistributionType.value.key;
    }
    this.checkPlanHasInventoryLevelData();
  }

  //Binding Min & Max budget value and appending it to the VO
  onMinMaxValueChange({ value, index, type, accessor }) {
    let distTypeControl = this.budgetDistributionType.value.key,
      distControl = this[type][index][distTypeControl];
    if (!distControl) {
      this[type][index][distTypeControl] = [];
    }
    this[type][index][distTypeControl][accessor] = Number(value);
    if (this[type][index][distTypeControl][1] !== 0) {
      this.minBudgetExceedsMaxBudget = this[type][index][distTypeControl][0] > this[type][index][distTypeControl][1];
    }
    this.positiveIntegerBudgetRequired = this[type][index][distTypeControl][accessor] <= 0;
    if (distControl === BudgetDistributionType.value.key) {
      this.budgetValueShouldBeLessThanHundred = this[type][index][distTypeControl][accessor] > 100;
    }
    this.patchTranformedDataToVO(this.openAuctionData, this.privateDealsData);
    this.calculateMinMaxBudgetTotal();
  }

  //Dropdown value change handling
  onBudgetDistributionTypeChange(e) {
    let oldVal = this.selectedBudgetDistributionType;
    if (typeof (e) === 'string' || typeof (e) === 'object') {
      if (e.value === this.selectedBudgetDistributionType || e === this.selectedBudgetDistributionType) {
        return;
      }
      this.selectedBudgetDistributionType = e.value || e;
      this.budgetDistributionTypeControl.patchValue(this.selectedBudgetDistributionType);
      this.checkPlanHasInventoryLevelData();
      this.resetMinMaxBudgetValues(oldVal !== this.selectedBudgetDistributionType);
    }
  }

  calculateMinMaxBudgetTotal() {
    if (this.budgetDistributionControl.value.length > 0) {
      let privateDealMinArray = []; let privateDealMaxArray = [];
      let openAuctionMinArray = []; let openAuctionMaxArray = [];
      let minO = 0; let maxO = 0; let minP = 0; let maxP = 0;
      //Private Deals
      forEach(map(this.budgetDistributionControl.value, 'privateDeals'), x => {
        if (x) {
          privateDealMinArray.push(x.value[0]);
          privateDealMaxArray.push(x.value[1]);
        }
      });
      if (privateDealMinArray && privateDealMinArray.length > 0) {
        minO = privateDealMinArray.reduce((a, b) => a + b, 0);
        maxO = privateDealMaxArray.reduce((a, b) => a + b, 0);
      }

      //Open Auctions
      forEach(map(this.budgetDistributionControl.value, 'openExchange'), x => {
        openAuctionMinArray.push(x.value[0]);
        openAuctionMaxArray.push(x.value[1]);
      });
      if (openAuctionMinArray && openAuctionMinArray.length) {
        minP = openAuctionMinArray.reduce((a, b) => a + b, 0);
        maxP = openAuctionMaxArray.reduce((a, b) => a + b, 0);
      }
      this.minValueTotal = minO + minP;
      this.maxValueTotal = maxO + maxP;
    }
  }

  resetMinMaxBudgetValues(reset: boolean) {
    if (!reset) return;
    let resettedData = forEach(this.budgetDistributionControl.value, (x) => {
      x.privateDeals.value = [0, 0];
      x.openExchange.value = [0, 0];
      x.privateDeals.allocated = 0;
      x.openExchange.allocated = 0;
    });
    this.budgetDistributionControl.patchValue(resettedData);
    this.calculateMinMaxBudgetTotal();
    this.minBudgetExceedsMaxBudget = this.positiveIntegerBudgetRequired = this.budgetValueShouldBeLessThanHundred = false;
    this.checkPlanHasInventoryLevelData();
  }

  checkPlanHasInventoryLevelData = () => {
    this.hasAnyInventoryLevelData = (this.inventorySourceControl.value.length > 0 || this.privateDealsControl.value.length > 0 || this.pgDealsControl.value.length > 0 || this.amobeePreferredDealsControl.value.length > 0) ? true : false;
    if (this.hasAnyInventoryLevelData && this.inventoryData && this.inventoryData.length) {
      if (this.currentInvSrcData && this.currentInvSrcData.length > 0 && this.inventorySourceControl.value && this.inventorySourceControl.value.length > 0) {
        this.openAuctionData = this.prepareOpenAuctionDataForAllocationTable(this.currentInvSrcData, this.budgetDistributionControl.value);
      }
      if (this.pgDeals && this.pgDeals.length > 0 && this.pgDealsControl.value && this.pgDealsControl.value.length > 0) {
        this.pgDealData = this.pgDeals.filter(x => this.pgDealsControl.value.includes(x.name));
      }
      if (this.amobeePreferredDeals && this.amobeePreferredDeals.length > 0 && this.amobeePreferredDealsControl.value && this.amobeePreferredDealsControl.value.length > 0) {
        let selectedAmobeePreferredDeals = [];
        forEach(this.amobeePreferredDeals, (x) => {
          if (this.amobeePreferredDealsControl.value.find(y => x.publisherDealId === y.id)) {
            selectedAmobeePreferredDeals.push(x);
          }
        });
        this.amobeePreferredDealData = this.prepareDealDataForAllocationTable(selectedAmobeePreferredDeals, this.budgetDistributionControl.value);
      }
      if (this.privateDeals && this.privateDeals.length > 0 && this.privateDealsControl.value && this.privateDealsControl.value.length > 0 || this.amobeePreferredDealData && this.amobeePreferredDealData.length > 0) {
        let selectedPrivateDeals = [];
        forEach(this.privateDeals, (x) => {
          if (this.privateDealsControl.value.find(y => x.publisherDealId === y.id)) {
            selectedPrivateDeals.push(x)
          }
        });
        if (this.amobeePreferredDealsControl.value.length > 0 && this.privateDealsControl.value.length > 0) {
          let privateDealsData = this.prepareDealDataForAllocationTable(selectedPrivateDeals, this.budgetDistributionControl.value);
          this.privateDealsData = this.amobeePreferredDealData.concat(privateDealsData);
        } else if (!this.amobeePreferredDealsControl.value.length && this.privateDealsControl.value.length > 0) {
          this.privateDealsData = this.prepareDealDataForAllocationTable(selectedPrivateDeals, this.budgetDistributionControl.value);
        } else if (this.amobeePreferredDealsControl.value.length && !this.privateDealsControl.value.length) {
          this.privateDealsData = this.amobeePreferredDealData;
        } else {
          this.privateDealsData = [];
        }
      } else {
        this.privateDealsData = [];
      }
      if ((this.openAuctionData && this.openAuctionData.length > 0 || this.privateDealsData && this.privateDealsData.length > 0) && this.isNew) {
        this.patchTranformedDataToVO(this.openAuctionData, this.privateDealsData);
      } else if (this.openAuctionData && this.openAuctionData.length > 0 && this.privateDealsData && this.privateDealsData.length > 0 && !this.isNew) {
        this.patchTranformedDataToVO(this.openAuctionData, this.privateDealsData);
      } else if (this.openAuctionData && !this.openAuctionData.length && this.privateDealsData && this.privateDealsData.length > 0) {
        this.patchTranformedDataToVO(this.openAuctionData, this.privateDealsData);
      } else if (this.openAuctionData && this.openAuctionData.length > 0 && this.privateDealsData && !this.privateDealsData.length) {
        this.patchTranformedDataToVO(this.openAuctionData, this.privateDealsData);
      }
      this.calculateMinMaxBudgetTotal();
    } else {
      this.openAuctionData = this.privateDealsData = [];
      this.patchTranformedDataToVO(this.openAuctionData, this.privateDealsData);
    }
  }

  //Prepare budget distribution data array to be patch to VO
  patchTranformedDataToVO(openAuction = [], privateDeals = []) {
    let combinedData = [...openAuction, ...privateDeals];
    if (combinedData && combinedData.length) {
      let budgetDistribution = map(uniqBy(combinedData, 'name'), item => {
        let openExchangeObj = combinedData.find(x => x.name === item.name && !x.isPrivateDeals) || {},
          privateObj = combinedData.find(x => x.name === item.name && x.isPrivateDeals) || {};
        return {
          invSrcId: item.name,
          openExchange: {
            value: openExchangeObj.value || [0, 0],
            allocated: openExchangeObj.allocated || 0
          },
          privateDeals: {
            dealIds: map(privateObj.deals, 'publisherDealId'),
            value: privateObj.value || [0, 0],
            allocated: privateObj.allocated || 0
          }
        }
      });
      this.budgetDistributionControl.patchValue(budgetDistribution);
    }
    if (!this.hasAnyInventoryLevelData && isEmpty(combinedData)) {
      this.budgetDistributionControl.patchValue([]);
    }
  }

  //Prepare private Deals data to be displayed on UI
  prepareDealDataForAllocationTable(data, vo) {
    let invSrcMapOfSelectedDeals = uniq(map(data, 'invSource'));
    let obj = [];
    forEach(invSrcMapOfSelectedDeals, (i) => {
      let currentDeals = data.filter(x => x.invSource === i);
      let matchedDeal = vo.find(o => o.invSrcId === currentDeals[0].invSourceId);
      let o = {
        label: i,
        deals: currentDeals,
        value: (vo.length > 0 && matchedDeal && matchedDeal.privateDeals && matchedDeal.privateDeals.value) ? matchedDeal.privateDeals.value : [0, 0],
        allocated: (vo.length > 0 && matchedDeal && matchedDeal.privateDeals && matchedDeal.privateDeals.allocated) ? matchedDeal.privateDeals.allocated : 0,
        name: currentDeals[0].invSourceId,
        isPrivateDeals: true
      }
      obj.push(o);
    });
    return obj;
  }

  //Prepare openAuctionData to be dispalyed on UI
  prepareOpenAuctionDataForAllocationTable(data, vo) {
    let invSrcdata = data.filter(x => this.inventorySourceControl.value.includes(x.name));
    forEach(invSrcdata, (x) => {
      let matchedOpenAuction = vo.find(o => o.invSrcId === x.name);
      x.value = (vo.length > 0 && matchedOpenAuction && matchedOpenAuction.openExchange && matchedOpenAuction.openExchange.value) ? matchedOpenAuction.openExchange.value : [0, 0];
      x.allocated = (vo.length > 0 && matchedOpenAuction && matchedOpenAuction.openExchange && matchedOpenAuction.openExchange.allocated) ? matchedOpenAuction.openExchange.allocated : 0;
    });
    return invSrcdata;
  }

  get amobeePreferredDealsControl() {
    return this.formArray.controls[this.group].get("preferredDeals") as FormControl;
  }

  get privateDealsControl() {
    return this.formArray.controls[this.group].get("deals") as FormControl;
  }

  get pgDealsControl() {
    return this.formArray.controls[this.group].get("pgDeals") as FormControl;
  }

  get inventorySourceControl() {
    return this.formArray.controls[this.group].get("inventorySources") as FormControl;
  }

  get advanceBudgetDistributionControl() {
    return this.formArray.controls[this.group].get("budgetDistribution") as FormGroup;
  }

  get budgetDistributionTypeControl() {
    return this.advanceBudgetDistributionControl.get("distributionType") as FormControl;
  }

  get budgetDistributionControl() {
    return this.advanceBudgetDistributionControl.get("budgetDistribution") as FormArray;
  }

  returnZero() {
    return 0;
  }

}
